import { CalculationType } from "../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ConditionField,
  ListGeneralSettings,
  ReportField,
  SortField,
} from "../../../../../../api/types/svTypes.ts";
import { MetaItemType } from "../../../../../../api/types/svTypes.ts";
import { isCustomColumn } from "../../utilities/fieldsHelper.ts";
import type { ListFieldState } from "../types/fieldsState.types.ts";

export type ListLedgerChange = {
  ledger?: string;
  conditions: ConditionField[];
  fields: ReportField[];
  sorts: SortField[];
  settings: ListGeneralSettings;
  removedItems: string[];
};

const supportsLedger = (field: ConditionField | ReportField | SortField, ledger: string) =>
  field.meta.itemType !== MetaItemType.DIMENSION || field.meta.supportedLedgerNames?.includes(ledger) === true;

const fieldDependsOnRemovedItem = (
  field: ReportField,
  originalFieldIds: Set<string>,
  survivingFieldIds: Set<string>,
  originalConditionIds: Set<string>,
  survivingConditionIds: Set<string>
) => {
  if (field.config.dependsFromFieldId) {
    return (
      originalFieldIds.has(field.config.dependsFromFieldId) && !survivingFieldIds.has(field.config.dependsFromFieldId)
    );
  }
  if (!isCustomColumn(field) || !field.config.formulaParameters) {
    return false;
  }

  const originalIds =
    field.config.calculationType === CalculationType.ConditionValue ? originalConditionIds : originalFieldIds;
  const survivingIds =
    field.config.calculationType === CalculationType.ConditionValue ? survivingConditionIds : survivingFieldIds;
  return field.config.formulaParameters.some((id) => originalIds.has(id) && !survivingIds.has(id));
};

const getCaption = (field: ConditionField | ReportField | SortField) => {
  if ("customLabel" in field.config && field.config.customLabel) {
    return field.config.customLabel;
  }
  if ("caption" in field.config && field.config.caption) {
    return field.config.caption;
  }
  return field.meta.caption;
};

export const createListLedgerChange = (state: ListFieldState, ledger?: string): ListLedgerChange => {
  if (!ledger) {
    return {
      ledger,
      conditions: state.conditions,
      fields: state.fields,
      sorts: state.sorts,
      settings: state.settings,
      removedItems: [],
    };
  }

  const originalFieldIds = new Set(state.fields.map((field) => field.config.guid));
  const originalConditionIds = new Set(state.conditions.map((condition) => condition.config.guid));
  const conditions = state.conditions.filter((condition) => supportsLedger(condition, ledger));
  const survivingConditionIds = new Set(conditions.map((condition) => condition.config.guid));
  let fields = state.fields.filter((field) => supportsLedger(field, ledger));

  let previousLength = -1;
  while (previousLength !== fields.length) {
    previousLength = fields.length;
    const survivingFieldIds = new Set(fields.map((field) => field.config.guid));
    fields = fields.filter(
      (field) =>
        !fieldDependsOnRemovedItem(
          field,
          originalFieldIds,
          survivingFieldIds,
          originalConditionIds,
          survivingConditionIds
        )
    );
  }

  const survivingFieldIds = new Set(fields.map((field) => field.config.guid));
  const sorts = state.sorts.filter(
    (sort) =>
      supportsLedger(sort, ledger) && (!originalFieldIds.has(sort.config.id) || survivingFieldIds.has(sort.config.id))
  );
  const settings = {
    ...state.settings,
    freezedColumns: state.settings.freezedColumns.filter((column) => survivingFieldIds.has(column)),
  };
  const removedItems = [
    ...state.conditions.filter((condition) => !conditions.includes(condition)),
    ...state.fields.filter((field) => !fields.includes(field)),
    ...state.sorts.filter((sort) => !sorts.includes(sort)),
  ]
    .map(getCaption)
    .filter((caption, index, captions) => captions.indexOf(caption) === index);

  return { ledger, conditions, fields, sorts, settings, removedItems };
};
